import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import shot383 from './slideshow/homeallinone.png';
import shot773 from './slideshow/773shots_so.png';
import shot511 from './slideshow/511shots_so.png';
import shot432 from './slideshow/432shots_so.png';
import shot760 from './slideshow/newclient.png';
import shot50 from './slideshow/50shots_so.png'
import notes from './slideshow/259shots_so.png'
import addclient from './slideshow/44shots_so.png'
import startbooking from './slideshow/215shots_so.png'
import location from './slideshow/720shots_so.png'
import record from './slideshow/599shots_so.png'
import analytics from './slideshow/81shots_so.png'
import paymentmethodsdemo from './slideshow/paymentmethodsdemo.png'
import createinvoice from './slideshow/createinvoice.png'
import clientreceiptinvoice from './slideshow/clientreceiptinvoice.png'

// Slides can optionally override how their device is sized:
//   deviceWidth — fraction of the stage width the phone should occupy,
//                 overriding DEVICE_WIDTH below.
//   scale       — multiplier on top of that, for small nudges (1.05, 0.95…).
// Neither is needed normally: the device is measured out of the image itself.
const slides = [
  {
    title: 'All-in-One Solution',
    caption: 'Run your packages, bookings, and sessions from one app.',
    image: shot383,
  },
  {
    title: 'Payments Simplified',
    caption: 'Clients pay from one link, using your preferred payment methods.',
    image: clientreceiptinvoice,
  },
  {
    title: 'Automated Invoicing',
    caption: 'Send one-time invoices or set up automatic weekly and monthly billing.',
    image: createinvoice,
  },
  {
    title: 'Apple & Google Calendars',
    caption: 'Keep your bookings automatically synced across calendars.',
    image: shot511,
  },
  {
    title: 'Easy Client Checkout',
    caption: 'Offer services through a beautifully simple mobile experience.',
    image: shot773,
  },
  {
    title: 'Flexible Location',
    caption: 'Set a location or let clients choose where the service takes place.',
    image: location,
  },
  {
    title: 'Overnight Services',
    caption: 'Offer overnight services and let clients book extended stays with ease.',
    image: shot50,
  },
  {
    title: 'Payment Methods',
    caption: 'Integrate the payment methods you already use within your dashboard.',
    image: paymentmethodsdemo,
  },
  {
    title: 'Record Payments',
    caption: 'Record payments from clients and keep your balances up to date.',
    image: record,
  },
  {
    title: 'Analytics Dashboard',
    caption: 'Track sales, revenue, and business activity all in one place.',
    image: analytics,
  },
  {
    title: 'Client Notes',
    caption: 'Track client progress, save session notes, and notify clients with text updates.',
    image: notes,
  },
  {
    title: 'Text Announcements',
    caption: 'Keep clients updated on ongoing events with text announcements.',
    image: shot432,
  },
  {
    title: 'Add Clients',
    caption: 'Add your existing clients and manage them all from one dashboard.',
    image: addclient,
  },
  {
    title: 'Start Booking Immediately',
    caption: 'Track client sessions from your dashboard and start booking right away.',
    image: startbooking,
  },
  {
    title: 'Client Portal',
    caption: 'Clients can log in to track bookings or purchase more services with you.',
    image: shot760,
  }
];

const APP_STORE_URL = 'https://apps.apple.com/us/app/verca/id6751428364';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.arthurhakobyandev.StreamChatDev';

// ---------------------------------------------------------------------------
// Sizing
//
// Each mockup is exported with a different amount of empty space around the
// phone, so scaling the whole canvas to a fixed width makes some devices look
// large and others small. Instead every image is measured once on load: we
// find the bounding box of its non-transparent pixels — the device — and lay
// out from that. Every slide then renders its phone at the same width, dead
// centre, with nothing clipped.
//
// Two rules keep the slides consistent with each other:
//   1. The stage is sized from the TALLEST device across all slides, not from
//      whichever one happens to be showing. One fixed box, no height jumps,
//      and no slide can ever overflow the box it's drawn into.
//   2. A mockup that can't be measured (opaque background, or an image the
//      browser won't let us read) is fitted whole instead of being trimmed.
//      It may sit a little smaller inside the stage, but it is never cropped.
// ---------------------------------------------------------------------------

// How wide the phone itself should be, as a fraction of the stage. Raise it to
// make the devices bigger across the board; this is the main dial to turn.
const DEVICE_WIDTH = 0.74;

// Breathing room above and below the device, as a fraction of its height.
const VERTICAL_PADDING = 0.04;

// The stage will never grow past this fraction of the viewport height. If a
// device would exceed it, every slide scales down together rather than crop.
const MAX_STAGE_VH = 0.62;

// Alpha below this counts as empty space. Bump it up if a mockup has a soft
// drop shadow that's being counted as part of the device.
const ALPHA_THRESHOLD = 24;

// Logs each mockup's dimensions and detected device box to the console.
const DEBUG_SIZING = false;

// How far down the page you have to be before the header will hide. Set to 0
// so the very first pixel of downward scroll tucks it away. Raise it if you'd
// rather the nav stay put for a bit first.
const NAV_HIDE_THRESHOLD = 0;

const FULL_BOX = { x: 0, y: 0, w: 1, h: 1 };

// Scans a decoded image for non-transparent pixels and returns the device box
// as fractions of the image's own width and height. Returns null if the pixels
// can't be read, or if there's no transparent margin to isolate a device from.
const scanImage = (probe) => {
  try {
    // Scan a downscaled copy — the box only needs to be accurate to within a
    // pixel or two of the final rendered size.
    const SAMPLE = 320;
    const ratio = Math.min(1, SAMPLE / Math.max(probe.naturalWidth, probe.naturalHeight));
    const w = Math.max(1, Math.round(probe.naturalWidth * ratio));
    const h = Math.max(1, Math.round(probe.naturalHeight * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0, w, h);

    const { data } = ctx.getImageData(0, 0, w, h);
    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (data[(y * w + x) * 4 + 3] < ALPHA_THRESHOLD) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < 0) return null;

    const box = {
      x: minX / w,
      y: minY / h,
      w: (maxX - minX + 1) / w,
      h: (maxY - minY + 1) / h,
    };

    // An edge-to-edge box means the image has no transparent margin at all, so
    // there's no device to isolate — treat it as unmeasurable and fit it whole.
    const measured = box.w < 0.98 || box.h < 0.98;
    return { box, measured };
  } catch {
    // Tainted canvas — the image loaded but can't be read.
    return null;
  }
};

// The scan runs on a separate probe image, never on the one being displayed,
// so a failure here can't affect the visible slide. It tries with crossOrigin
// first and falls back to a plain load, which is what same-origin bundled
// assets need when the dev server doesn't send CORS headers.
const measureImage = (src) =>
  new Promise((resolve) => {
    const attempt = (withCors) => {
      const probe = new Image();
      if (withCors) probe.crossOrigin = 'anonymous';
      probe.onerror = () => (withCors ? attempt(false) : resolve(null));
      probe.onload = () => {
        const result = scanImage(probe);
        if (result || !withCors) resolve(result);
        else attempt(false);
      };
      probe.src = src;
    };

    attempt(true);
  });

const Demo = () => {
  const [current, setCurrent] = useState(0);
  const [meta, setMeta] = useState({});
  const [viewport, setViewport] = useState({ stageWidth: 0, height: 0 });
  const [navHidden, setNavHidden] = useState(false);
  const navigate = useNavigate();

  const stageRef = useRef(null);
  const imgRefs = useRef([]);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const lastScrollY = useRef(0);

  const isLastSlide = current === slides.length - 1;

  const measureViewport = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    setViewport({ stageWidth: stage.clientWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    measureViewport();
    window.addEventListener('resize', measureViewport);
    window.addEventListener('orientationchange', measureViewport);
    return () => {
      window.removeEventListener('resize', measureViewport);
      window.removeEventListener('orientationchange', measureViewport);
    };
  }, [measureViewport]);

  // Records the image's aspect ratio as soon as it decodes, so the stage can
  // size itself before the pixel scan finishes. Ratio is written LAST so a
  // scan result that landed first can't blank it out again.
  const captureRatio = useCallback((el, i) => {
    const { naturalWidth, naturalHeight } = el;
    if (!naturalWidth) return;
    const ratio = naturalHeight / naturalWidth;
    setMeta((prev) => {
      if (prev[i]?.ratio === ratio) return prev;
      return {
        ...prev,
        [i]: { box: FULL_BOX, measured: false, ...prev[i], ratio },
      };
    });
  }, []);

  // Cached images can finish decoding before React attaches onLoad, so the ref
  // callback picks up anything that's already complete.
  const registerImg = useCallback(
    (el, i) => {
      imgRefs.current[i] = el;
      if (el && el.complete) captureRatio(el, i);
    },
    [captureRatio]
  );

  // Scan every mockup once, then upgrade its entry with the real device box.
  useEffect(() => {
    let cancelled = false;

    slides.forEach((slide, i) => {
      measureImage(slide.image).then((result) => {
        if (cancelled || !result) return;
        if (DEBUG_SIZING) {
          const { box } = result;
          console.log(
            `[slide ${i}] ${slide.title} — device box ` +
              `x:${box.x.toFixed(3)} y:${box.y.toFixed(3)} ` +
              `w:${box.w.toFixed(3)} h:${box.h.toFixed(3)} ` +
              `measured:${result.measured}`
          );
        }
        // Merge, never replace: whatever ratio onLoad already recorded stays.
        setMeta((prev) => ({ ...prev, [i]: { ...prev[i], ...result } }));
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Turns the measured boxes into one shared stage height plus an absolute
  // position for every image, so each device lands at the same width in the
  // same place — and the stage doesn't resize as you swipe between slides.
  const layout = useMemo(() => {
    const { stageWidth, height: viewportHeight } = viewport;
    if (!stageWidth) return null;

    // Natural (unshrunk) geometry per slide, from that slide's OWN metrics.
    // A slide with no metrics yet stays null and simply isn't drawn — it never
    // borrows another slide's aspect ratio, which is what cropped slide 3.
    const natural = slides.map((slide, i) => {
      const m = meta[i];
      if (!m?.ratio) return null;

      // Unmeasurable mockups treat the whole canvas as the device, so they're
      // fitted whole rather than blindly trimmed.
      const box = m.measured ? m.box : FULL_BOX;
      const target = (slide.deviceWidth ?? DEVICE_WIDTH) * (slide.scale ?? 1);
      const imgWidth = (stageWidth * target) / box.w;
      const imgHeight = imgWidth * m.ratio;

      return { imgWidth, imgHeight, box, deviceHeight: imgHeight * box.h };
    });

    const known = natural.filter(Boolean);
    if (!known.length) return null;

    // One box for every slide: tall enough for the tallest device we know of.
    const tallest = Math.max(...known.map((g) => g.deviceHeight));
    let stageHeight = tallest * (1 + VERTICAL_PADDING);

    // Too tall for the viewport? Scale every slide down by the same factor so
    // the devices keep their relative sizes instead of one getting cropped.
    let shrink = 1;
    const maxHeight = viewportHeight * MAX_STAGE_VH;
    if (maxHeight > 0 && stageHeight > maxHeight) {
      shrink = maxHeight / stageHeight;
      stageHeight = maxHeight;
    }

    const room = stageHeight / (1 + VERTICAL_PADDING);

    const images = natural.map((g) => {
      if (!g) return null;

      // Guard for the window before every slide has reported in: if a late
      // arrival is taller than the stage built without it, fit it rather than
      // let it spill. Once all three are known this is always 1.
      const fit = Math.min(1, room / (g.deviceHeight * shrink));
      const s = shrink * fit;
      const width = g.imgWidth * s;
      const height = g.imgHeight * s;

      return {
        width,
        // Centre the device box — not the canvas — in the stage.
        left: stageWidth / 2 - width * (g.box.x + g.box.w / 2),
        top: stageHeight / 2 - height * (g.box.y + g.box.h / 2),
      };
    });

    return { stageHeight, images };
  }, [meta, viewport]);

  // Slide the header out of the way on scroll down so it never sits on top of
  // the slide title / caption, and bring it back as soon as you scroll up.
  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const handleScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;

      // Ignore iOS rubber-band overscroll above the top of the page.
      if (y < 0) return;

      // Any downward movement at all hides it; any upward movement (or
      // landing back at the very top) brings it straight back.
      if (delta > 0 && y > NAV_HIDE_THRESHOLD) setNavHidden(true);
      else if (delta < 0 || y === 0) setNavHidden(false);

      lastScrollY.current = y;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goTo = (index) => {
    const last = slides.length - 1;
    if (index < 0) setCurrent(0);
    else if (index > last) setCurrent(last);
    else setCurrent(index);
  };

  const next = () => goTo(current + 1);
  const prev = () => goTo(current - 1);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    const threshold = 50;
    if (touchDeltaX.current > threshold) prev();
    else if (touchDeltaX.current < -threshold) next();
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  return (
    <div className="demo-root">
      {/* Fixed header — same treatment as the ServicesPage nav.
          Wordmark sits top-left and routes back to Home.
          Collapses upward while scrolling down. */}
      <nav className={`demo-nav ${navHidden ? 'is-hidden' : ''}`}>
        <div className="demo-nav-inner">
          <button type="button" className="demo-logo" onClick={() => navigate('/')}>
            <span className="logo-text">Verca</span>
          </button>
        </div>
      </nav>

      <div className="slideshow">
        {/* Title */}
        <h2 className="slide-title" key={`title-${current}`}>
          {slides[current].title}
        </h2>

        {/* Subtitle — directly below the title */}
        <p className="slide-caption" key={`cap-${current}`}>
          {slides[current].caption}
        </p>

        {/* Device mockups sit directly under the subtitle. The stage is as
            tall as the tallest device plus a little padding, and stays that
            height for every slide. */}
        <div
          className="slide-stage"
          ref={stageRef}
          style={{ height: layout ? `${layout.stageHeight}px` : undefined }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="slide-track"
            style={{ transform: `translateX(-${current * 100}%)` }}
          >
            {slides.map((slide, i) => {
              const placement = layout?.images[i];
              return (
                <div className="slide" key={i}>
                  <img
                    className="slide-img"
                    ref={(el) => registerImg(el, i)}
                    src={slide.image}
                    alt={slide.title}
                    draggable="false"
                    decoding="async"
                    style={
                      placement
                        ? {
                            width: `${placement.width}px`,
                            left: `${placement.left}px`,
                            top: `${placement.top}px`,
                          }
                        : { visibility: 'hidden' }
                    }
                    onLoad={(e) => captureRatio(e.target, i)}
                  />
                </div>
              );
            })}
          </div>

          {/* Edge tap / click controls */}
          <button
            className="edge edge-left"
            onClick={prev}
            disabled={current === 0}
            aria-label="Previous slide"
          >
            <i className="fas fa-chevron-left" />
          </button>
          <button
            className="edge edge-right"
            onClick={next}
            disabled={isLastSlide}
            aria-label="Next slide"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>

        {/* Dots */}
        <div className="dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === current ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Store links: quiet while stepping through the screens, then they
            take over as the page's action on the last slide. */}
        <div className={`store-section ${isLastSlide ? 'is-final' : ''}`}>
          <div className="store-buttons">
            <a className="store-btn" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple" />
              <span className="store-btn-text">
                <span className="store-btn-small">Download on the</span>
                <span className="store-btn-large">App Store</span>
              </span>
            </a>

            <a className="store-btn" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              <i className="fab fa-google-play" />
              <span className="store-btn-text">
                <span className="store-btn-small">Get it on</span>
                <span className="store-btn-large">Google Play</span>
              </span>
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }

        .demo-root {
          --text-primary: #000000;
          --text-secondary: #444444;
          --text-tertiary: #767676;
          --hairline: #e6e6e6;
          --hairline-soft: #ededed;
          --accent: #6be891;

          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          /* DemoDark's 20px top / 32px bottom, with the fixed nav's ~63px
             added on top since this version has a header to clear. */
          padding: 83px 16px 32px;
          background: #ffffff;
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI',
            Roboto, 'Helvetica Neue', Arial, sans-serif;
          color: var(--text-primary);
          -webkit-font-smoothing: antialiased;
          position: relative;
          overflow: hidden;
        }

        .demo-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 1.25rem 1.5rem;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          border-bottom: 1px solid var(--hairline-soft);
          transform: translateY(0);
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }

        /* Tucks fully out of frame — border included — while scrolling down. */
        .demo-nav.is-hidden {
          transform: translateY(-101%);
        }

        .demo-nav-inner {
          max-width: 640px;
          margin: 0 auto;
          display: flex;
          align-items: center;
        }

        .demo-logo {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font-family: inherit;
          transition: opacity 0.2s ease;
        }

        .demo-logo:hover {
          opacity: 0.65;
        }

        .demo-logo:focus-visible {
          outline: 2px solid #000000;
          outline-offset: 4px;
          border-radius: 4px;
        }

        .logo-text {
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1;
          color: #000000;
        }

        .slideshow {
          width: 100%;
          max-width: 380px;
          display: flex;
          flex-direction: column;
        }

        .slide-title {
          font-size: clamp(24px, 7vw, 30px);
          font-weight: 800;
          letter-spacing: -0.8px;
          line-height: 1.15;
          text-align: center;
          color: #000000;
          margin-bottom: 10px;
          animation: rise 0.45s ease both;
        }

        /* Subtitle directly below the title — no gap before the image. */
        .slide-caption {
          font-size: 16px;
          line-height: 1.55;
          color: var(--text-tertiary);
          text-align: center;
          margin-bottom: 0;
          min-height: 50px;
          animation: rise 0.45s ease both;
        }

        /* No card frame — the mockups carry their own device frame. Height is
           set inline to fit the tallest device, and the carousel is clipped
           horizontally during transitions. */
        .slide-stage {
          position: relative;
          width: 100%;
          overflow: hidden;
          touch-action: pan-y;
          transition: height 0.45s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide-track {
          display: flex;
          align-items: stretch;
          height: 100%;
          transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide {
          position: relative;
          min-width: 100%;
          height: 100%;
          overflow: hidden;
        }

        /* Positioned from the measured device box rather than the canvas, so
           the empty margin falls outside the stage and gets clipped while the
           device stays whole and centred. Aspect ratio is never touched: the
           width is set and the height follows. */
        .slide-img {
          position: absolute;
          height: auto;
          max-width: none;
          user-select: none;
          transition: width 0.45s cubic-bezier(0.4, 0, 0.2, 1),
            left 0.45s cubic-bezier(0.4, 0, 0.2, 1),
            top 0.45s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .edge {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: 1px solid var(--hairline);
          background: rgba(255, 255, 255, 0.92);
          color: #000000;
          font-size: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.25s ease;
          z-index: 2;
        }

        .edge-left {
          left: 4px;
        }
        .edge-right {
          right: 4px;
        }

        .edge:hover:not(:disabled) {
          background: #000000;
          border-color: #000000;
          color: #ffffff;
        }

        .edge:focus-visible {
          outline: 2px solid #000000;
          outline-offset: 2px;
        }

        .edge:disabled {
          opacity: 0;
          pointer-events: none;
        }

        .dots {
          display: flex;
          gap: 8px;
          justify-content: center;
          margin-top: 20px;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: none;
          padding: 0;
          background: #dcdcdc;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .dot:hover {
          background: #b4b4b4;
        }

        .dot.active {
          width: 26px;
          border-radius: 5px;
          background: #000000;
        }

        .dot:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 3px;
        }

        /* The bottom margin gives the page enough extra scroll height for
           mobile browsers to collapse their URL bar. */
        .store-section {
          margin-top: 28px;
          margin-bottom: 35vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .store-buttons {
          display: flex;
          gap: 12px;
          width: 100%;
          justify-content: center;
        }

        .store-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: #ffffff;
          border: 1px solid var(--hairline);
          border-radius: 14px;
          color: #000000;
          text-decoration: none;
          flex: 1;
          justify-content: center;
          transition: background 0.3s cubic-bezier(0.4, 0, 0.2, 1),
            border-color 0.3s ease, color 0.3s ease,
            transform 0.35s cubic-bezier(0.34, 1.45, 0.64, 1);
        }

        .store-btn:hover {
          border-color: #000000;
        }

        .store-btn:focus-visible {
          outline: 2px solid #000000;
          outline-offset: 3px;
        }

        .store-section.is-final .store-btn {
          background: #000000;
          border-color: #000000;
          color: #ffffff;
          animation: pop 0.5s cubic-bezier(0.34, 1.45, 0.64, 1) both;
        }

        .store-section.is-final .store-btn:nth-child(2) {
          animation-delay: 0.08s;
        }

        .store-section.is-final .store-btn:hover {
          background: #1f1f1f;
          border-color: #1f1f1f;
          transform: scale(1.04);
        }

        .store-btn i {
          font-size: 26px;
          flex-shrink: 0;
          color: currentColor;
        }

        .store-btn-text {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .store-btn-small {
          font-size: 11px;
          font-weight: 500;
          opacity: 0.7;
          letter-spacing: 0.3px;
          line-height: 1.2;
          text-transform: uppercase;
        }

        .store-btn-large {
          font-size: 17px;
          font-weight: 600;
          letter-spacing: 0.2px;
          line-height: 1.2;
        }

        @keyframes rise {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pop {
          from {
            opacity: 0;
            transform: scale(0.86);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .slide-track,
          .slide-stage,
          .slide-img,
          .slide-title,
          .slide-caption,
          .store-section.is-final .store-btn,
          .store-section.is-final .store-btn:hover {
            animation: none;
            transition: none;
          }

          .slide-title,
          .slide-caption,
          .store-section.is-final .store-btn,
          .store-section.is-final .store-btn:hover {
            transform: none;
          }

          /* Header still collapses, it just snaps instead of sliding. */
          .demo-nav {
            transition: none;
          }
        }
      `}</style>

      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&display=swap"
        rel="stylesheet"
      />
    </div>
  );
};

export default Demo;